import { useEffect, useState, useMemo } from 'react';
import { productsApi, categoriesApi, sizeGuidesApi } from '@/lib/api/arrex/catalog';
import { useApp } from '@/contexts/AppContext';
import { formatPrice, localized, imgUrl } from '@/lib/format';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ToggleField } from '@/components/ui/toggle-field';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Loader2, X, FolderTree, ChevronDown, Activity, Ruler,
  Search, CheckCircle2, Clock, Sparkles, Archive, ExternalLink, Star,
  Tag, Layers, type LucideIcon
} from 'lucide-react';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { readItems, field } from '@/lib/api/normalize';

type Product = any;
type Relation = { main_category_id: string | null; subcategory_id: string | null };
const STATUSES = ['in_stock', 'coming_soon', 'limited', 'archived'];

const getStatusBadge = (status: string, t: any) => {
  switch (status) {
    case 'in_stock':
      return {
        label: t('admin.status.in_stock', { defaultValue: 'In Stock' }),
        icon: CheckCircle2,
        className: 'bg-emerald-600 text-white shadow-xs border border-emerald-400/40',
        dotClass: 'bg-emerald-200 animate-pulse',
      };
    case 'coming_soon':
      return {
        label: t('admin.status.coming_soon', { defaultValue: 'Coming Soon' }),
        icon: Clock,
        className: 'bg-amber-600 text-white shadow-xs border border-amber-400/40',
        dotClass: 'bg-amber-200',
      };
    case 'limited':
      return {
        label: t('admin.status.limited', { defaultValue: 'Limited' }),
        icon: Sparkles,
        className: 'bg-purple-600 text-white shadow-xs border border-purple-400/40',
        dotClass: 'bg-purple-200',
      };
    case 'archived':
      return {
        label: t('admin.status.archived', { defaultValue: 'Archived' }),
        icon: Archive,
        className: 'bg-zinc-700 text-white shadow-xs border border-zinc-500/40',
        dotClass: 'bg-zinc-300',
      };
    default:
      return {
        label: t(`admin.status.${status}`, { defaultValue: status }),
        icon: Activity,
        className: 'bg-primary text-on-primary shadow-xs border border-primary/40',
        dotClass: 'bg-white',
      };
  }
};

/** Convert any string to a safe URL slug: lowercase, hyphens, no special chars. */
const toSlug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[\u2013\u2014\u2012]/g, '-')   // en-dash, em-dash → hyphen
    .replace(/[^a-z0-9\u0600-\u06ff]+/g, '-') // anything not alphanumeric/arabic → hyphen
    .replace(/^-+|-+$/g, '');                  // trim leading/trailing hyphens

const isUnsafeSlug = (s: string) => /[\s%&?#=+]/.test(s);
const empty = {
  name_en: '', name_ar: '', slug: '', description_en: '', description_ar: '',
  base_price_egp: 0, status: 'in_stock', is_featured: false,
  sizes: ['S', 'M', 'L', 'XL'], category_id: null as string | null, main_image_url: '',
  discount_enabled: false, discounted_price_egp: '' as any,
  limited_text_en: '', limited_text_ar: '',
  size_guide_id: null as string | null, show_size_guide: true,
};

export default function AdminProducts() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [cats, setCats] = useState<any[]>([]);
  const [guides, setGuides] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  const [relations, setRelations] = useState<Relation[]>([]);
  const [openRel, setOpenRel] = useState<number | null>(0);

  // Search & Filters
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  /** Maps a backend product row onto the snake_case shape this form uses. */
  const fromApi = (p: any) => {
    const hasDiscount = p.compareAtPrice && Number(p.compareAtPrice) > Number(p.price);
    const base = hasDiscount ? p.compareAtPrice : p.price;
    const disc = hasDiscount ? p.price : '';
    const statusVal = field<string>(p, 'status') ?? p.status ?? 'in_stock';
    return {
      ...p,
      name_en: field<string>(p, 'name_en') ?? p.name ?? '',
      name_ar: field<string>(p, 'name_ar') ?? '',
      slug: p.slug ?? '',
      description_en: field<string>(p, 'description_en') ?? p.description ?? '',
      description_ar: field<string>(p, 'description_ar') ?? '',
      base_price_egp: Number(base ?? 0),
      discount_enabled: !!hasDiscount,
      discounted_price_egp: disc,
      status: statusVal,
      is_featured: !!(field<boolean>(p, 'is_featured', 'isFeatured') ?? false),
      main_image_url: imgUrl(
        field(p, 'main_image_url', 'image_url', 'thumbnail_url', 'mainImageUrl', 'imageUrl') ??
        p.images,
      ),
      sizes: p.sizes || ['S', 'M', 'L', 'XL'],
    };
  };

  const load = () =>
    productsApi.adminList({ pageSize: 150, sort: 'newest' }).then((res) => setProducts(readItems(res).map(fromApi)));

  useEffect(() => {
    load();
    categoriesApi.adminList().then((res) => setCats(readItems(res)));
    sizeGuidesApi.list().then((res) => setGuides(readItems(res)));
  }, []);

  const startNew = () => { setForm({ ...empty }); setRelations([{ main_category_id: null, subcategory_id: null }]); setOpen(true); };
  const startEdit = async (p: Product) => {
    const mapped = fromApi(p);
    setForm({
      ...mapped,
      status: mapped.status,
      limited_text_en: field<string>(p, 'limited_text_en', 'limitedTextEn') || '',
      limited_text_ar: field<string>(p, 'limited_text_ar', 'limitedTextAr') || '',
      size_guide_id: field<string>(p, 'size_guide_id', 'sizeGuideId') ?? null,
      show_size_guide: field<boolean>(p, 'show_size_guide', 'showSizeGuide') ?? true
    });
    const items = p.categoryId ? [{ mainCategoryId: p.categoryId, subcategoryId: p.subcategoryId }] : [];
    setRelations((items && items.length > 0) ? items.map((r: any) => ({ main_category_id: r.mainCategoryId || r.main_category_id, subcategory_id: r.subcategoryId || r.subcategory_id })) : [{ main_category_id: null, subcategory_id: null }]);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name_en || !form.slug) return toast.error('Name and slug required');
    if (form.discount_enabled && (!form.discounted_price_egp || Number(form.discounted_price_egp) >= Number(form.base_price_egp))) {
      return toast.error('Discounted price must be lower than original price');
    }
    const validRels = relations.filter(r => r.main_category_id);
    if (validRels.length === 0) return toast.error(t('admin.atLeastOneCategory'));
    const mains = validRels.map(r => r.main_category_id);
    if (new Set(mains).size !== mains.length) return toast.error(t('admin.duplicateMainCategory'));

    setSaving(true);
    const basePrice = Number(form.base_price_egp) || 0;
    const discPrice = form.discount_enabled && form.discounted_price_egp !== '' ? Number(form.discounted_price_egp) : null;

    const payload: any = {
      nameEn: form.name_en,
      nameAr: form.name_ar || form.name_en,
      slug: form.slug,
      descriptionEn: form.description_en || '',
      descriptionAr: form.description_ar || '',
      price: discPrice !== null ? discPrice : basePrice,
      compareAtPrice: discPrice !== null ? basePrice : null,
      status: form.status,
      isFeatured: !!form.is_featured,
      sizes: form.sizes,
      mainImageUrl: form.main_image_url || null,
      imageUrl: form.main_image_url || null,
      images: form.main_image_url ? [form.main_image_url] : [],
      limitedTextEn: form.limited_text_en || null,
      limitedTextAr: form.limited_text_ar || null,
      sizeGuideId: form.size_guide_id || null,
      showSizeGuide: !!form.show_size_guide,
      categoryId: validRels[0]?.main_category_id || null,
      subcategoryId: validRels[0]?.subcategory_id || null,
      relations: validRels.map(r => ({ mainCategoryId: r.main_category_id, subcategoryId: r.subcategory_id || null })),
    };
    try {
      if (form.id) await productsApi.update(form.id, payload);
      else await productsApi.create(payload);
      toast.success(form.id ? 'Product updated successfully' : 'Product created successfully');
      setOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await productsApi.remove(deleteTarget.id);
      toast.success('Product deleted');
      setDeleteTarget(null);
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Filtered products list
  const filteredProducts = useMemo(() => {
    if (!products) return null;
    return products.filter(p => {
      // Status filter
      if (selectedStatus !== 'all' && p.status !== selectedStatus) return false;
      // Category filter
      if (selectedCategory !== 'all' && p.categoryId !== selectedCategory && p.category_id !== selectedCategory) return false;
      // Search filter
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const nameEn = (p.name_en || '').toLowerCase();
        const nameAr = (p.name_ar || '').toLowerCase();
        const slug = (p.slug || '').toLowerCase();
        const ref = (p.ref || '').toLowerCase();
        if (!nameEn.includes(q) && !nameAr.includes(q) && !slug.includes(q) && !ref.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [products, selectedStatus, selectedCategory, search]);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: products?.length || 0 };
    STATUSES.forEach(s => {
      counts[s] = products?.filter(p => p.status === s).length || 0;
    });
    return counts;
  }, [products]);

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t('admin.products.title')}</h1>
            {products && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                {products.length} {t('common.products', { defaultValue: 'Products' })}
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {t('admin.productsPage.subtitle', { defaultValue: 'Manage your catalog, edit stock status, pricing, and details.' })}
          </p>
        </div>

        <Button
          onClick={startNew}
          className="h-10 px-4 rounded-xl bg-primary text-on-primary hover:bg-primary/90 shadow-sm transition-all gap-2"
        >
          <Plus className="w-4 h-4" />
          <span className="font-medium text-sm">{t('admin.products.add')}</span>
        </Button>
      </div>

      {/* Filter & Search Bar */}
      <div className="surface-card rounded-2xl p-3 sm:p-4 border border-border/70 shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('admin.productsPage.searchPlaceholder', { defaultValue: 'Search by name, slug, or ref…' })}
              className="pl-9 h-10 rounded-xl bg-background/80 border-border/70 focus-visible:ring-primary/40 text-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="w-full md:w-56">
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-border/70 bg-background/80 text-sm font-medium hover:border-primary/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="all">{t('admin.allCategories', { defaultValue: 'All Categories' })}</option>
              {cats.filter(c => !c.parent_id).map(c => (
                <option key={c.id} value={c.id}>
                  {lang === 'ar' ? (c.nameAr || c.name_ar) : (c.nameEn || c.name_en)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-1 no-scrollbar text-xs">
          <button
            type="button"
            onClick={() => setSelectedStatus('all')}
            className={cn(
              'px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer',
              selectedStatus === 'all'
                ? 'bg-primary text-on-primary shadow-xs'
                : 'bg-muted/60 hover:bg-muted text-muted-foreground'
            )}
          >
            <span>{t('admin.table.all', { defaultValue: 'All' })}</span>
            <span className={cn('text-[10px] px-1.5 py-0.2 rounded-full', selectedStatus === 'all' ? 'bg-white/20 text-white' : 'bg-background/80 text-foreground')}>
              {statusCounts.all}
            </span>
          </button>

          {STATUSES.map(s => {
            const isSelected = selectedStatus === s;
            const badgeInfo = getStatusBadge(s, t);
            return (
              <button
                key={s}
                type="button"
                onClick={() => setSelectedStatus(s)}
                className={cn(
                  'px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer',
                  isSelected
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'bg-muted/60 hover:bg-muted text-muted-foreground'
                )}
              >
                <span>{badgeInfo.label}</span>
                <span className={cn('text-[10px] px-1.5 py-0.2 rounded-full', isSelected ? 'bg-white/20 text-white' : 'bg-background/80 text-foreground')}>
                  {statusCounts[s] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="surface-card rounded-2xl p-3.5 sm:p-4 border border-border/60 flex items-center gap-3.5">
              <div className="w-18 h-18 sm:w-20 sm:h-20 rounded-xl bg-muted/60 animate-pulse shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
                <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
                <div className="h-3 bg-muted animate-pulse rounded w-1/3" />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="w-8 h-8 rounded-xl bg-muted animate-pulse" />
                <div className="w-8 h-8 rounded-xl bg-muted animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="surface-card rounded-2xl p-12 text-center border border-dashed border-border/80 flex flex-col items-center justify-center gap-3">
          <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <Layers className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">
              {search || selectedStatus !== 'all' || selectedCategory !== 'all'
                ? t('admin.noMatchingProducts', { defaultValue: 'No products match your filters' })
                : t('admin.noProductsYet', { defaultValue: 'No products added yet' })}
            </h3>
            <p className="text-xs text-muted-foreground max-w-sm">
              {search || selectedStatus !== 'all' || selectedCategory !== 'all'
                ? 'Try adjusting your search terms or clearing status/category filters.'
                : 'Add your first product to start building your luxury catalog.'}
            </p>
          </div>
          {search || selectedStatus !== 'all' || selectedCategory !== 'all' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSearch(''); setSelectedStatus('all'); setSelectedCategory('all'); }}
              className="mt-2 rounded-xl text-xs"
            >
              {t('admin.clearFilters', { defaultValue: 'Clear Filters' })}
            </Button>
          ) : (
            <Button onClick={startNew} size="sm" className="mt-2 rounded-xl text-xs gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              {t('admin.products.add')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
          {filteredProducts.map(p => {
            const orig = Number(p.base_price_egp);
            const disc = p.discount_enabled && p.discounted_price_egp != null ? Number(p.discounted_price_egp) : null;
            const pct = disc != null && orig > 0 ? Math.round(((orig - disc) / orig) * 100) : 0;
            const badgeInfo = getStatusBadge(p.status, t);
            const mainCat = cats.find(c => c.id === p.categoryId || c.id === p.category_id);

            return (
              <div
                key={p.id}
                className="surface-card rounded-2xl p-3 sm:p-3.5 border border-border/80 hover:border-primary/50 bg-card hover:bg-surface-container-low/40 transition-all duration-200 shadow-xs hover:shadow-md flex items-center gap-3 sm:gap-3.5 min-w-0 relative group"
              >
                {/* Thumbnail Image */}
                <div className="relative w-18 h-18 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-border/70 bg-muted/40 shrink-0 shadow-2xs">
                  {p.main_image_url ? (
                    <img
                      src={imgUrl(p.main_image_url)}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                      <Layers className="w-6 h-6" />
                    </div>
                  )}
                  {p.is_featured && (
                    <div className="absolute top-1 left-1 bg-amber-500 text-white p-0.5 rounded-full shadow-xs" title="Featured">
                      <Star className="w-2.5 h-2.5 fill-current" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {mainCat && (
                      <span className="text-[10px] font-semibold text-secondary uppercase tracking-wider">
                        {localized(mainCat, 'name', lang)}
                      </span>
                    )}
                    {p.ref && (
                      <span className="text-[9px] font-mono text-muted-foreground/60">
                        {p.ref}
                      </span>
                    )}
                  </div>

                  <p className="font-semibold text-sm sm:text-base leading-snug truncate text-foreground group-hover:text-primary transition-colors">
                    {localized(p, 'name', lang)}
                  </p>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs sm:text-sm font-bold text-primary whitespace-nowrap">
                      {formatPrice(disc ?? orig, lang)}
                    </span>
                    {disc != null && (
                      <span className="text-[10px] sm:text-xs text-muted-foreground line-through whitespace-nowrap">
                        {formatPrice(orig, lang)}
                      </span>
                    )}
                    {disc != null && (
                      <Badge className="bg-destructive text-destructive-foreground text-[9px] font-bold px-1.5 py-0.2 rounded-md">
                        -{pct}%
                      </Badge>
                    )}
                  </div>

                  <div className="pt-0.5">
                    <span className={cn('inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-medium px-2 py-0.5 rounded-full border', badgeInfo.className)}>
                      <span className={cn('w-1.5 h-1.5 rounded-full', badgeInfo.dotClass)} />
                      <span>{badgeInfo.label}</span>
                    </span>
                  </div>
                </div>

                {/* Actions (Always visible) */}
                <div className="flex flex-col gap-1.5 shrink-0 self-center">
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 rounded-xl border-border/80 hover:border-primary hover:bg-primary/10 hover:text-primary transition-all shadow-2xs cursor-pointer"
                    onClick={() => startEdit(p)}
                    title={t('admin.table.edit')}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 rounded-xl border-border/80 hover:border-destructive hover:bg-destructive/10 text-destructive hover:text-destructive transition-all shadow-2xs cursor-pointer"
                    onClick={() => setDeleteTarget(p)}
                    title={t('admin.table.delete')}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
          <DialogHeader><DialogTitle>{form.id ? t('admin.edit') : t('admin.products.addTitle')}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.productsPage.nameEn', { defaultValue: 'Name (EN)' })}</Label>
              <Input
                value={form.name_en}
                onChange={e => {
                  const name = e.target.value;
                  // Auto-fill slug only for new products (no id yet)
                  setForm((prev: any) => ({
                    ...prev,
                    name_en: name,
                    slug: prev.id ? prev.slug : toSlug(name),
                  }));
                }}
              />
            </div>
            <div><Label>{t('admin.productsPage.nameAr', { defaultValue: 'Name (AR)' })}</Label><Input value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} /></div>
            <div className="md:col-span-2">
              <Label>{t('admin.productsPage.slugLabel', { defaultValue: 'Slug' })}</Label>
              <Input
                value={form.slug}
                onChange={e => setForm({ ...form, slug: e.target.value })}
                placeholder="my-product-name"
                className={isUnsafeSlug(form.slug) ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {isUnsafeSlug(form.slug) && (
                <p className="text-xs text-destructive mt-1">
                  ⚠️ Slug contains spaces or special characters — this causes broken URLs on mobile.
                  Suggested: <button type="button" className="underline font-mono" onClick={() => setForm((p: any) => ({ ...p, slug: toSlug(p.slug) }))}>{toSlug(form.slug)}</button>
                </p>
              )}
              {form.slug && !isUnsafeSlug(form.slug) && (
                <p className="text-xs text-muted-foreground mt-1 font-mono">/product/{form.slug}</p>
              )}
            </div>
             <div><Label>{t('admin.productsPage.originalPrice', { defaultValue: 'Original Price (EGP)' })}</Label><Input type="number" value={form.base_price_egp} onChange={e => setForm({ ...form, base_price_egp: e.target.value })} /></div>
            
            {/* Status Selection Cards */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">{t('admin.productsPage.status', { defaultValue: 'Product Status' })}</Label>
              <div className="grid grid-cols-2 gap-2">
                {STATUSES.map(s => {
                  const badgeInfo = getStatusBadge(s, t);
                  const isSelected = form.status === s;
                  const IconComp = badgeInfo.icon;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm((prev: any) => ({ ...prev, status: s }))}
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-2 rounded-xl border text-start transition-all cursor-pointer select-none',
                        isSelected
                          ? 'border-primary bg-primary/10 shadow-xs ring-1 ring-primary/30 font-semibold'
                          : 'border-border/70 hover:border-border hover:bg-muted/40 text-muted-foreground'
                      )}
                    >
                      <div className={cn('p-1 rounded-md shrink-0', isSelected ? 'bg-primary text-on-primary' : 'bg-muted text-muted-foreground')}>
                        <IconComp className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className={cn('block text-xs truncate', isSelected ? 'text-foreground font-semibold' : 'text-muted-foreground')}>
                          {badgeInfo.label}
                        </span>
                      </div>
                      {isSelected && <span className="text-primary text-xs font-bold shrink-0">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-2 surface-card rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base">{t('admin.productsPage.discount', { defaultValue: 'Discount' })}</Label>
                <ToggleField checked={!!form.discount_enabled} onCheckedChange={(v) => setForm({ ...form, discount_enabled: v })} hideLabel />
              </div>
              {form.discount_enabled && (
                <div>
                  <Label>{t('admin.productsPage.discountedPrice', { defaultValue: 'Discounted Price (EGP)' })}</Label>
                  <Input type="number" value={form.discounted_price_egp}
                    onChange={e => setForm({ ...form, discounted_price_egp: e.target.value })}
                    placeholder="e.g. 800" />
                  {form.discounted_price_egp !== '' && Number(form.discounted_price_egp) > 0 && Number(form.base_price_egp) > 0 && (
                    <p className="text-xs text-success mt-1">
                      {Math.round(((Number(form.base_price_egp) - Number(form.discounted_price_egp)) / Number(form.base_price_egp)) * 100)}% off
                    </p>
                  )}
                </div>
              )}
            </div>

            {form.status === 'limited' && (
              <div className="md:col-span-2 surface-card rounded-lg p-4 border border-accent/40 space-y-3">
                <Label className="text-base text-accent">{t('admin.productsPage.limitedBadgeText', { defaultValue: 'Limited Badge Text' })}</Label>
                <p className="text-xs text-muted-foreground">{t('admin.productsPage.limitedBadgeDesc', { defaultValue: 'Customize what the limited badge says. Leave blank to use the default "Limited Edition".' })}</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>{t('admin.productsPage.limitedTextEn', { defaultValue: 'Limited Text (EN)' })}</Label>
                    <Input value={form.limited_text_en || ''} onChange={e => setForm({ ...form, limited_text_en: e.target.value })}
                      placeholder="e.g. Only 5 Left" />
                  </div>
                  <div>
                    <Label>{t('admin.productsPage.limitedTextAr', { defaultValue: 'Limited Text (AR)' })}</Label>
                    <Input value={form.limited_text_ar || ''} onChange={e => setForm({ ...form, limited_text_ar: e.target.value })}
                      placeholder="مثال: متبقي 5 فقط" dir="rtl" />
                  </div>
                </div>
              </div>
            )}

            <div className="md:col-span-2 surface-card rounded-lg p-4 border border-border space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-base flex items-center gap-2"><FolderTree className="w-4 h-4 text-primary" />{t('admin.categoryRelations')}</Label>
              </div>
              {(() => {
                const allMains = cats.filter(c => !c.parent_id);
                const usedMains = relations.map(r => r.main_category_id).filter(Boolean) as string[];
                return (
                  <div className="space-y-2">
                    {relations.map((rel, idx) => {
                      const availableMains = allMains.filter(c => c.id === rel.main_category_id || !usedMains.includes(c.id));
                      const subs = rel.main_category_id ? cats.filter(c => c.parent_id === rel.main_category_id) : [];
                      const mainObj = allMains.find(c => c.id === rel.main_category_id);
                      const subObj = subs.find(c => c.id === rel.subcategory_id);
                      const mainLabel = mainObj ? (lang === 'ar' ? (mainObj.nameAr || mainObj.name_ar) : (mainObj.nameEn || mainObj.name_en)) : t('admin.selectCategory');
                      const subLabel = subObj ? (lang === 'ar' ? (subObj.nameAr || subObj.name_ar) : (subObj.nameEn || subObj.name_en)) : null;
                      const isOpen = openRel === idx;
                      return (
                        <Collapsible key={idx} open={isOpen} onOpenChange={(v) => setOpenRel(v ? idx : null)}>
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                'w-full flex items-center justify-between gap-3 h-14 px-4 rounded-lg border border-border bg-background/80 hover:border-primary/50 transition-all text-start',
                                isOpen && 'border-primary/60 bg-background'
                              )}
                            >
                              <span className="flex items-center gap-3 min-w-0 flex-1">
                                <span className="rounded-md bg-primary/10 text-primary text-[10px] font-mono uppercase tracking-wider px-2 py-1 shrink-0">#{idx + 1}</span>
                                <span className="min-w-0 flex-1">
                                  <span className="block text-sm font-semibold leading-tight truncate">{mainLabel}</span>
                                  <span className="block text-[11px] text-muted-foreground truncate">
                                    {subLabel ? subLabel : rel.main_category_id ? t('admin.selectSubcategory') : t('admin.categoryRelation') + ' ' + (idx + 1)}
                                  </span>
                                </span>
                              </span>
                              <span className="flex items-center gap-1 shrink-0">
                                {relations.length > 1 && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.stopPropagation(); setRelations(relations.filter((_, i) => i !== idx)); if (openRel === idx) setOpenRel(null); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setRelations(relations.filter((_, i) => i !== idx)); } }}
                                    className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
                                    aria-label="Remove"
                                  >
                                    <X className="w-4 h-4" />
                                  </span>
                                )}
                                <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
                              </span>
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                            <div className="mt-2 grid sm:grid-cols-2 gap-3">
                              <CategoryPicker
                                tone="primary"
                                label={t('admin.mainCategory')}
                                placeholder={t('admin.selectCategory')}
                                options={availableMains.map(c => ({ id: c.id, label: lang === 'ar' ? (c.nameAr || c.name_ar) : (c.nameEn || c.name_en) }))}
                                value={rel.main_category_id}
                                onChange={(v) => setRelations(relations.map((r, i) => i === idx ? { main_category_id: v, subcategory_id: null } : r))}
                              />
                              <CategoryPicker
                                tone="accent"
                                label={t('admin.subcategory')}
                                placeholder={!rel.main_category_id ? t('admin.selectCategoryFirst') : subs.length === 0 ? t('admin.noSubcategories') : t('admin.selectSubcategory')}
                                options={subs.map(c => ({ id: c.id, label: lang === 'ar' ? (c.nameAr || c.name_ar) : (c.nameEn || c.name_en) }))}
                                value={rel.subcategory_id}
                                disabled={!rel.main_category_id || subs.length === 0}
                                onChange={(v) => setRelations(relations.map((r, i) => i === idx ? { ...r, subcategory_id: v || null } : r))}
                              />
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                    <Button type="button" variant="outline" size="sm"
                      disabled={usedMains.length >= allMains.length}
                      onClick={() => { setRelations([...relations, { main_category_id: null, subcategory_id: null }]); setOpenRel(relations.length); }}>
                      <Plus className="w-4 h-4 me-1" />{t('admin.addAnotherCategory')}
                    </Button>
                  </div>
                );
              })()}
            </div>

            <div className="md:col-span-2 space-y-2">
              <div className="flex justify-between items-center">
                <Label>{t('admin.productsPage.sizes', { defaultValue: 'Sizes' })}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={(form.sizes || []).includes('One Size')}
                  onClick={() => setForm({ ...form, sizes: ['S', 'M', 'L', 'XL', '2XL'] })}
                  className="text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('admin.productsPage.selectSet', { defaultValue: 'Select S to 2XL' })}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* One Size — exclusive toggle */}
                {(() => {
                  const isOneSize = (form.sizes || []).includes('One Size');
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setForm({ ...form, sizes: isOneSize ? [] : ['One Size'] });
                        }}
                        className={cn(
                          "h-10 px-4 rounded-md border text-sm font-medium transition-all",
                          isOneSize
                            ? "border-secondary bg-secondary/10 text-secondary"
                            : "border-border bg-background hover:bg-muted text-muted-foreground"
                        )}
                      >
                        {t('admin.productsPage.oneSize', { defaultValue: 'One Size' })}
                      </button>

                      {/* Divider */}
                      <div className="w-px h-10 bg-border/60 mx-1" />

                      {/* Standard sizes — disabled when One Size is active */}
                      {['S', 'M', 'L', 'XL', '2XL'].map((sz) => {
                        const isSelected = (form.sizes || []).includes(sz);
                        return (
                          <button
                            key={sz}
                            type="button"
                            disabled={isOneSize}
                            onClick={() => {
                              const current = form.sizes || [];
                              const next = current.includes(sz)
                                ? current.filter((s: string) => s !== sz)
                                : [...current, sz];
                              setForm({ ...form, sizes: next });
                            }}
                            className={cn(
                              "h-10 px-4 rounded-md border text-sm font-medium transition-all",
                              isOneSize
                                ? "border-border/40 bg-muted/20 text-muted-foreground/40 cursor-not-allowed"
                                : isSelected
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "border-border bg-background hover:bg-muted text-muted-foreground"
                            )}
                          >
                            {sz}
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="md:col-span-2"><Label>{t('admin.productsPage.descriptionEn', { defaultValue: 'Description (EN)' })}</Label><Textarea value={form.description_en || ''} onChange={e => setForm({ ...form, description_en: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>{t('admin.productsPage.descriptionAr', { defaultValue: 'Description (AR)' })}</Label><Textarea value={form.description_ar || ''} onChange={e => setForm({ ...form, description_ar: e.target.value })} /></div>
            <div className="md:col-span-2">
              <Label>{t('admin.productsPage.mainImage', { defaultValue: 'Main Image' })}</Label>
              <div className="mt-1">
                <ImageUploader
                  value={form.main_image_url}
                  onChange={(url) => setForm({ ...form, main_image_url: url })}
                  bucket="product-images"
                  aspect="product"
                />
              </div>
            </div>

            <div className="md:col-span-2 surface-card rounded-lg p-4 border border-border space-y-3">
              <Label className="text-base">{t('admin.productsPage.sizeGuide', { defaultValue: 'Size Guide' })}</Label>
              <CategoryPicker
                tone="accent"
                label=""
                placeholder={t('admin.productsPage.none', { defaultValue: 'None' })}
                icon={Ruler}
                options={[{ id: 'none', label: t('admin.productsPage.none', { defaultValue: 'None' }) }, ...guides.map(g => ({ id: g.id, label: lang === 'ar' ? g.name_ar : g.name_en }))]}
                value={form.size_guide_id || 'none'}
                onChange={(v) => setForm({ ...form, size_guide_id: v === 'none' ? null : v })}
              />
              <ToggleField
                checked={!!form.show_size_guide}
                onCheckedChange={(v) => setForm({ ...form, show_size_guide: v })}
                label={t('admin.productsPage.showSizeGuide', { defaultValue: 'Show size guide on product page' })}
                labelPosition="end"
                variant="field"
              />
            </div>
          </div>
          <DialogFooter className="flex flex-row items-center justify-end gap-3 mt-6">
            <Button variant="outline" className="flex-1 sm:flex-initial rounded-full h-12" onClick={() => setOpen(false)}>{t('admin.cancel')}</Button>
            <Button className="flex-1 sm:flex-initial rounded-full h-12 bg-primary text-on-primary hover:bg-primary/90" onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 me-1 animate-spin" />}
              {t('admin.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent dir={lang === 'ar' ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {t('admin.deleteProduct')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && t('admin.deleteProductConfirm', { name: localized(deleteTarget, 'name', lang) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : (t('admin.delete') || 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CategoryPicker({
  tone, label, placeholder, options, value, onChange, disabled, icon: Icon = FolderTree,
}: {
  tone: 'primary' | 'accent';
  label: string;
  placeholder: string;
  options: { id: string; label: string }[];
  value: string | null;
  onChange: (v: string) => void;
  disabled?: boolean;
  icon?: LucideIcon;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.id === value);
  const toneBg = tone === 'primary' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent';
  return (
    <Collapsible open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'w-full flex items-center justify-between gap-3 h-14 px-4 rounded-lg border border-border bg-background/80 hover:border-primary/50 transition-all text-start disabled:opacity-60 disabled:cursor-not-allowed',
            open && !disabled && 'border-primary/60 bg-background'
          )}
        >
          <span className="flex items-center gap-3 min-w-0 flex-1">
            <span className={cn('rounded-md p-1.5 shrink-0', toneBg)}>
              <Icon className="w-4 h-4" />
            </span>
            <span className="min-w-0 flex-1">
              {label && <span className="block text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</span>}
              <span className="block text-sm font-semibold leading-tight truncate">{selected ? selected.label : placeholder}</span>
            </span>
          </span>
          <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform shrink-0', open && !disabled && 'rotate-180')} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
        <div className="mt-2 rounded-lg border border-border bg-background/40 p-1.5 max-h-60 overflow-y-auto space-y-1">
          {options.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">{placeholder}</div>
          )}
          {options.map(o => {
            const isSel = o.id === value;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); }}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-start text-sm transition-colors',
                  isSel ? 'bg-primary/15 text-foreground font-semibold' : 'hover:bg-muted/60'
                )}
              >
                <span className="truncate">{o.label}</span>
                {isSel && <span className="text-primary text-xs">✓</span>}
              </button>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
